import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Crown, ShieldCheck } from "lucide-react";

const Row = ({ a, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/creator-applications/${a.id}/decide`, { approve, reason: reason.trim() });
      toast.success(approve ? `${a.stage_name} approved${data.launch_offer ? " · founding creator (100% for 3 months)" : ""}` : "Application rejected");
      onDone(a.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); setBusy(false); }
  };
  return (
    <div data-testid={`application-${a.id}`} className="rounded-2xl border border-white/8 bg-surface p-5 space-y-3">
      <div className="flex items-center gap-3">
        <img src={a.user.photo_url || "https://images.unsplash.com/photo-1637059880830-59a90102de77"} alt="" className="w-11 h-11 rounded-full object-cover" />
        <div className="min-w-0">
          <p className="text-sm">{a.stage_name} <span className="text-text-muted">· {a.country}</span></p>
          <p className="text-xs text-text-muted break-all">{a.email}{a.user.account_email && a.user.account_email !== a.email ? ` (account: ${a.user.account_email})` : ""}</p>
        </div>
      </div>
      <p className={`text-[11px] flex items-center gap-1.5 ${a.user.age_verified ? "text-emerald-400" : "text-amber-300"}`}>
        <ShieldCheck size={12} /> {a.user.age_verified ? "ID verified 18+" : "18+ confirmed, ID check still to do"}
      </p>
      <input data-testid={`application-reason-${a.id}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
        placeholder="Reason if rejecting (sent to applicant)" className="w-full bg-transparent border-b border-white/15 py-2 text-sm focus:border-gold outline-none" />
      <div className="flex gap-2">
        <button data-testid={`application-approve-${a.id}`} disabled={busy} onClick={() => decide(true)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-emerald-600/80 hover:bg-emerald-600 text-xs uppercase tracking-[0.15em] disabled:opacity-50"><Check size={14} /> Approve</button>
        <button data-testid={`application-reject-${a.id}`} disabled={busy} onClick={() => decide(false)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full border border-red-400/40 text-red-300 text-xs uppercase tracking-[0.15em] disabled:opacity-50"><X size={14} /> Reject</button>
      </div>
    </div>
  );
};

export const CreatorApplications = () => {
  const [rows, setRows] = useState(null);
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get("/admin/creator-applications").then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get("/creator-offer").then(({ data }) => setOffer(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="creator-applications">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Velluto Models</p>
          <h3 className="font-display text-3xl mt-1">Creator applications</h3>
          <p className="text-xs text-text-muted mt-1">Ad link: <span className="text-gold">vellutosociety.com/apply</span></p>
        </div>
        {offer && <p data-testid="applications-offer" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Crown size={14} /> {offer.claimed}/{offer.total} founding spots used</p>}
      </div>
      {!rows ? <p className="text-text-muted">Loading…</p>
        : rows.length === 0 ? <p data-testid="applications-empty" className="font-display italic text-2xl text-text-muted">No applications waiting.</p>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{rows.map((a) => <Row key={a.id} a={a} onDone={(id) => setRows((xs) => xs.filter((x) => x.id !== id))} />)}</div>}
    </div>
  );
};
